import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import AddSharpIcon from "@mui/icons-material/AddSharp";

import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import ButtonComponent from "../../components/Button.Component";
import Loader from "../../components/Molecules/Loader/Loader";
import ReadMoreLess from "../../components/ReadMoreLess";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { getFormattedDateTime } from "../../services/utils";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { paginatedResponseFormat } from "../../services/constants";
import useFetchPaginatedGenericResearchQueries from "../../hooks/useFetchPaginatedGenericResearchQueries";
import {
  deleteGenericResearchQueryById,
  reactivateGenericResearchQuery,
} from "../../services/ResearchQueriesService";
import { useToast } from "../../services/utils";
import ConfirmationDialog from "../../components/Dialogs/ConfirmationDialog";
import {
  IconDeleteBlueSvg,
  IconExecuteBase,
  IconEyeBase,
  IconEditBase,
  IconAudit,
  IconExportIconBlue,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import AssociateEntitiesDialog from "../../components/Dialogs/AssociateEntitiesDialog";
import { downloadGenericResearchQueryExportFile } from "../../services/utils";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
const DEFAULT_PAGE = parseInt(process.env.REACT_APP_DEFAULT_PAGE || "1", 10);

// Centralized Filter Configuration for Generic Research Query List
const GENERIC_RESEARCH_QUERY_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    status_filter: "Active",
    generic_research_query_id: null,
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: ["generic_research_query_id"],
    STRING_FIELDS: ["status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const GenericResearchQueryList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Ref to track if this is the initial mount
  const isInitialMount = useRef(true);

  // Ref to prevent circular updates between URL and state
  const isUpdatingFromURL = useRef(false);

  // Helper function to get filter state from URL (for initialization and URL changes)
  const getInitialFilterState = useCallback(() => {
    const defaultFilterState = {
      ...GENERIC_RESEARCH_QUERY_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    const urlFilters: any = {};

    // Handle ID fields (convert to number if present)
    GENERIC_RESEARCH_QUERY_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.forEach(
      (field) => {
        const value = searchParams.get(field);
        if (value && !isNaN(Number(value))) {
          urlFilters[field] = Number(value);
        }
      }
    );

    // Handle string fields
    GENERIC_RESEARCH_QUERY_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS.forEach(
      (field) => {
        const value = searchParams.get(field);
        if (value) {
          urlFilters[field] = value;
        }
      }
    );

    return { ...defaultFilterState, ...urlFilters };
  }, [searchParams]);

  // Initialize filter state from URL on mount
  const [searchFilterData, setSearchFilterData] = useState<any>(() =>
    getInitialFilterState()
  );

  // Helper function to serialize filter data to URL parameters
  const serializeFiltersToURL = useCallback(
    (filters: any) => {
      const newParams = new URLSearchParams(searchParams);

      // Remove all existing filter parameters first
      GENERIC_RESEARCH_QUERY_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
        newParams.delete(field);
      });

      // Add non-empty filter values to URL
      GENERIC_RESEARCH_QUERY_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
        const value = filters[field];
        if (value !== null && value !== undefined && value !== "") {
          if (typeof value === "string") {
            newParams.set(field, value);
          } else {
            newParams.set(field, String(value));
          }
        }
      });

      return newParams;
    },
    [searchParams]
  );

  // Permission checks for generic research query actions
  const canViewGenericResearchQuery = hasPermission(
    "generic_research_query",
    "view"
  );
  const canAddGenericResearchQuery = hasPermission(
    "generic_research_query",
    "add"
  );
  const canEditGenericResearchQuery = hasPermission(
    "generic_research_query",
    "edit"
  );
  const canDeleteGenericResearchQuery = hasPermission(
    "generic_research_query",
    "delete"
  );
  const canExecuteGenericResearchQuery = hasPermission(
    "generic_research_query",
    "execute"
  );
  const [
    openValidationExecutionConfirmation,
    setOpenValidationExecutionConfirmation,
  ] = useState(false);
  const [selectedExecutionToDelete, setSelectedExecutionToDelete] =
    useState<any>(null);

  // State for export functionality
  const [openAssociateEntitiesDialog, setOpenAssociateEntitiesDialog] =
    useState<boolean>(false);
  const [associateEntityId, setAssociateEntityId] = useState<number | null>(
    null
  );
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);

  const handleAssociateEntitiesDialog = () => {
    setOpenAssociateEntitiesDialog(true);
  };

  const [genericResearchQueryData, setGenericResearchQueryData] = useState<any>(
    paginatedResponseFormat
  );

  const [fetchedResourcesData, refetchGenericResearchQueries] =
    useFetchPaginatedGenericResearchQueries({
      setIsLoading,
      page,
      pSize,
      statusFilter: searchFilterData?.status_filter || "",
      genericResearchQueryId: searchFilterData?.generic_research_query_id || "",
    });

  useEffect(() => {
    if (fetchedResourcesData) {
      setGenericResearchQueryData(fetchedResourcesData);
    }
  }, [fetchedResourcesData]);

  // Update URL when filter state changes (State → URL)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if we're currently updating from URL to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Serialize current filter state to URL parameters
    const newParams = serializeFiltersToURL(searchFilterData);

    // Only update URL if the serialized params differ from current URL
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, serializeFiltersToURL, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → State) - for back/forward navigation
  useEffect(() => {
    // Extract current filter values from URL
    const urlFilterState = getInitialFilterState();

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? null
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? null
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes
    if (hasFilterChanges) {
      // Set flag to prevent the URL update effect from running
      isUpdatingFromURL.current = true;
      setSearchFilterData(urlFilterState);
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromURL.current = false;
      }, 0);
    }
  }, [getInitialFilterState]);

  // Reset page to 1 when search filter changes
  useEffect(() => {
    // Reset page when any filter changes
    if (
      searchFilterData?.status_filter !== undefined ||
      searchFilterData?.generic_research_query_id !== undefined
    ) {
      setPage(1);
    }
  }, [searchFilterData]);

  const handleAddNew = () => navigate("/research-query/add");
  const handleView = (id: number) => navigate(`/research-query/view/${id}`);
  // Option A routing: on Edit, fetch the detail to determine the query type.
  const handleEdit = (id: number) => {
    navigate(`/research-query/edit/${id}`);
  };

  // Handle reactivate generic research query
  const handleReactivate = async (id: number, name: string) => {
    setIsLoading(true);
    try {
      await reactivateGenericResearchQuery(id);
      showToast(
        `Generic Research Query "${name}" reactivated successfully`,
        "success"
      );
      // Refetch the list to update the UI
      refetchGenericResearchQueries();
    } catch (error) {
      showToast("Error reactivating generic research query", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const dataTableColumns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        minWidth: 20,
        flex: 0,
        renderCell: () => <></>,
        cellClassName: "no-padding-cell",
        headerClassName: "no-padding-cell",
      },
      {
        field: "id",
        headerName: "ID",
        minWidth: 50,
        renderCell: (params) => <span>{params.value}</span>,
      },
      {
        field: "name",
        headerName: "Query Name",
        minWidth: 150,
        renderCell: (params) => (
          <LimitChractersWithTooltip value={params.value} />
        ),
      },
      {
        field: "code",
        headerName: "Code",
        minWidth: 150,
        renderCell: (params) => (
          <LimitChractersWithTooltip value={params.value} />
        ),
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 150,
        flex: 1,
        groupable: false,
        renderCell: (params) => (
          <LimitChractersWithTooltip value={params.value} />
        ),
      },
      {
        field: "is_active",
        headerName: "Status",
        width: 100,
        align: "center",
        headerAlign: "center",
        groupable: false,
        renderCell: (params: any) => {
          return params.value ? (
            <Tooltip title="Active" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCheckCircleIconGreenSvg width={21} height={21} />
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Inactive" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCrossCircleIconSvg width={21} height={21} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "modified_by_user",
        headerName: "Modified By",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => (
          <LimitChractersWithTooltip value={params?.value?.username} />
        ),
      },
      {
        field: "updated_on",
        headerName: "Last Modified",
        minWidth: 180,
        flex: 1,
        renderCell: (params) => getFormattedDateTime(params.value),
      },
      {
        field: "action",
        headerName: "Actions",
        width: 160,
        align: "center",
        headerAlign: "center",
        groupable: false,
        renderCell: (params: any) => {
          const isActive = params.row.is_active;

          // Create dropdown menu items based on permissions and active status
          // Only show dropdown items for active records
          const dropdownItems = isActive
            ? [
                // Audit Research Query - requires view permission
                ...(canViewGenericResearchQuery
                  ? [
                      <IconButton
                        key="audit-research-query"
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/research-query/auditGenericResearchQuery/${params.row.id}`
                          )
                        }
                      >
                        <IconAudit /> Audit
                      </IconButton>,
                    ]
                  : []),
                // Export Research Query - requires view permission
                ...(canViewGenericResearchQuery
                  ? [
                      <IconButton
                        key="export"
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => {
                          setAssociateEntityId(params.row.id);
                          handleAssociateEntitiesDialog();
                        }}
                      >
                        <IconExportIconBlue />
                        Export
                      </IconButton>,
                    ]
                  : []),
                // Delete Research Query - requires delete permission and only for active records
                ...(canDeleteGenericResearchQuery
                  ? [
                      <IconButton
                        key="delete-research-query"
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => {
                          setSelectedExecutionToDelete(params.row);
                          setOpenValidationExecutionConfirmation(true);
                        }}
                      >
                        <IconDeleteBlueSvg /> Delete
                      </IconButton>,
                    ]
                  : []),
              ]
            : [];

          return (
            <>
              {/* For active records, show all action buttons */}
              {isActive && (
                <>
                  {/* Execute Query button - only for active records */}
                  {canExecuteGenericResearchQuery && (
                    <Tooltip title="Execute Query" placement="top" arrow>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/research-query/research-query-execution/${params.row.id}`
                          )
                        }
                      >
                        <IconExecuteBase />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* View button - for active records */}
                  {canViewGenericResearchQuery && (
                    <Tooltip title="View Research Query" placement="top" arrow>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => handleView(params.row.id)}
                      >
                        <IconEyeBase />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Edit button - for active records */}
                  {canEditGenericResearchQuery && (
                    <Tooltip title="Edit Research Query" placement="top" arrow>
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => handleEdit(params.row.id)}
                      >
                        <IconEditBase />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Dropdown menu with additional actions for active records */}
                  {dropdownItems.length > 0 && (
                    <DropdownMenu dropdownMenuItems={dropdownItems} />
                  )}
                </>
              )}

              {/* For inactive records, show ONLY Reactivate button */}
              {!isActive && canEditGenericResearchQuery && (
                <Tooltip
                  title="Reactivate Generic Research Query"
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      handleReactivate(params.row.id, params.row.name)
                    }
                  >
                    <IconReactivateSvg />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    ],
    [
      canViewGenericResearchQuery,
      canExecuteGenericResearchQuery,
      canDeleteGenericResearchQuery,
      canEditGenericResearchQuery,
      navigate,
      handleReactivate,
      handleEdit,
      handleView,
    ]
  );

  const handleValidationExecutionDelete = async () => {
    setOpenValidationExecutionConfirmation(false);
    if (!selectedExecutionToDelete) return;
    setIsLoading(true);
    try {
      await deleteGenericResearchQueryById(selectedExecutionToDelete.id);
      const updatedList = genericResearchQueryData?.items?.filter(
        (item: any) => item.id !== selectedExecutionToDelete.id
      );
      setGenericResearchQueryData((prev: any) => ({
        ...prev,
        items: updatedList,
      }));

      showToast(
        `${selectedExecutionToDelete.name} deleted successfully`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting query:", error);
      showToast("Failed to delete the query. Please try again.", "error");
    } finally {
      setIsLoading(false);
      setSelectedExecutionToDelete(null);
    }
  };

  return (
    <Box>
      {isLoading && <Loader isLoading />}

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["generic_research_query_id", "status_filter"]}
              primaryFields={["generic_research_query_id", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          <Grid
            item
            xs="auto"
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            {canAddGenericResearchQuery && (
              <ButtonComponent
                handelClickEvent={handleAddNew}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} /> Research Query
                </>
              </ButtonComponent>
            )}
          </Grid>
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={dataTableColumns || []}
          dataRows={genericResearchQueryData?.items || []}
          loading={isLoading}
          dataListTitle="Research Queries"
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={genericResearchQueryData?.total || 0}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          fitViewport
          singlePageMaxHeightDiff={338}
          isPaginationRequired={true}
          hideExportButton={true}
        />
      </FlexBetween>
      <ConfirmationDialog
        title={"Confirm removing Research Query"}
        dialogContent={"Are you sure you want to delete this query?"}
        handleCancel={() => setOpenValidationExecutionConfirmation(false)}
        openConfirmation={openValidationExecutionConfirmation}
        handleConfirm={handleValidationExecutionDelete}
        isShowClassMb0={false}
      />
      {isDownloadLoading && <Loader isLoading />}
      <AssociateEntitiesDialog
        openAssociateEntitiesDialog={openAssociateEntitiesDialog}
        setOpenAssociateEntitiesDialog={setOpenAssociateEntitiesDialog}
        associateEntityId={associateEntityId}
        downloadExportFile={downloadGenericResearchQueryExportFile}
        setIsDownloadLoading={setIsDownloadLoading}
        header="Export Generic Research Query"
      />
    </Box>
  );
};

export default GenericResearchQueryList;
